import { useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useMutation, useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FieldError } from "@/components/ui/field";
import {
  BaseForm,
  FormCheckbox,
  FormFileUpload,
  FormInput,
  FormMultiSelect,
  FormSelect,
  FormSkeleton,
} from "components/BaseForm";
import EmailPreview from "components/email/EmailPreview";
import Validate from "components/Validate";
import { DEFAULT_PROVIDER_OPTION, EMAIL_SERVICES } from "constants/Constants";
import {
  createTemplateOptions,
  decodeHtml,
  getResolvedProvider,
  isTemplateDisabled,
} from "helpers/emailHelper";
import { toSelectOptions } from "helpers/selectOptions";
import ContactService from "services/Contact";
import CampaignService from "services/Campaign";
import CustomEmailDomainsService from "services/CustomEmailDomains";
import EmailFooterService from "services/EmailFooters";
import EmailProviderService from "services/EmailProviders";
import EmailSignatureService from "services/EmailSignatures";
import EmailTemplateService from "services/EmailTemplate";
import SmtpAccountService from "services/SmtpAccounts";
import useAuthStore from "stores/useAuthStore";

// Inserts the signature and footer before the template's closing body tag, as
// the legacy preview does.
const buildPreviewHtml = (templateHtml, footerHtml, signatureHtml) => {
  if (!templateHtml) return "";
  let html = decodeHtml(templateHtml);
  let before = html.indexOf("</body>");
  if (before === -1) {
    html = `<html><head></head><body>${html}</body></html>`;
    before = html.indexOf("</body>");
  }
  const footer = footerHtml ? decodeHtml(footerHtml) : "";
  const signature = signatureHtml ? decodeHtml(signatureHtml) : "";
  return (
    html.substring(0, before) + signature + footer + html.substring(before)
  );
};

const buildSendPayload = (
  values,
  contactId,
  smtpAccounts,
  campaignEmailService
) => {
  const provider = getResolvedProvider(values.provider, campaignEmailService);
  // From-address resolution mirrors the legacy send flow: SMTP uses the
  // account's return address, Mailgun the username@domain.
  let fromUser = values.fromUser;
  if (provider === EMAIL_SERVICES.SMTP) {
    fromUser =
      smtpAccounts.find(account => account.id === values.smtp_config_id)
        ?.return_addr ?? "";
  } else if (provider === EMAIL_SERVICES.MAILGUN) {
    fromUser = `${values.fromUser}@${values.mailgunDomain}`;
  }

  return {
    contact_id: contactId,
    template: values.template,
    attachments: values.attachments,
    senderName: values.senderName,
    cc: values.cc,
    bcc: values.bcc,
    ccAccountManagers: values.ccAccountManagers,
    ccExternalAccountManagers: values.ccExternalAccountManagers,
    fromUser,
    smtp_config_id: values.smtp_config_id,
    provider,
    footerId: values.footerId,
    signatureId: values.signatureId,
    campaignId: values.campaignId,
    emailAddressIds: values.emailAddressIds,
    follow_up: false,
  };
};

const attachmentSchema = z.object({
  s3_key: z.string(),
  file_name: z.string(),
  file_size: z.number().optional(),
  file_content_type: z.string().optional(),
});

const buildSchema = campaignEmailService =>
  z
    .object({
      campaignId: z.number({
        required_error: "A campaign is required",
        invalid_type_error: "A campaign is required",
      }),
      provider: z.number({
        required_error: "An email provider is required",
        invalid_type_error: "An email provider is required",
      }),
      senderName: z.string().trim().min(1, "A sender name is required"),
      fromUser: z.string(),
      smtp_config_id: z.number().nullable(),
      mailgunDomain: z.string().nullable(),
      emailAddressIds: z
        .array(z.number())
        .min(1, "Select at least one recipient"),
      cc: z.string(),
      bcc: z.string(),
      ccAccountManagers: z.boolean(),
      ccExternalAccountManagers: z.boolean(),
      template: z.number({
        required_error: "A template is required",
        invalid_type_error: "A template is required",
      }),
      footerId: z.number().nullable(),
      signatureId: z.number().nullable(),
      attachments: z.array(attachmentSchema).default([]),
    })
    .superRefine((values, ctx) => {
      // Encodes the legacy send-readiness gate: the "From" identity required
      // depends on the resolved provider.
      const provider = getResolvedProvider(
        values.provider,
        campaignEmailService
      );
      if (values.provider != null && provider == null) {
        // "Campaign Default" chosen but the campaign has no service to resolve to.
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["provider"],
          message: "This campaign has no default email provider",
        });
      } else if (provider === EMAIL_SERVICES.SMTP) {
        if (values.smtp_config_id == null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["smtp_config_id"],
            message: "Select a From address",
          });
        }
      } else if (provider != null) {
        if (!Validate.emailUsername(values.fromUser)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["fromUser"],
            message: 'Enter a valid "From" username',
          });
        }
        if (provider === EMAIL_SERVICES.MAILGUN && !values.mailgunDomain) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["mailgunDomain"],
            message: "Select a domain",
          });
        }
      }
    });

const ProviderFields = ({
  provider,
  providerValue,
  providerOptions,
  smtpOptions,
  onProviderChange,
}) => {
  // The Mailgun domain list is only needed once Mailgun is chosen, so it loads
  // on demand rather than up front.
  const { data: mailgunDomains = [] } = useQuery({
    queryKey: ["mailgun-verified-domains"],
    queryFn: () => CustomEmailDomainsService.getVerifiedDomains(),
    enabled: provider === EMAIL_SERVICES.MAILGUN,
  });

  const mailgunOptions = mailgunDomains.map(domain => ({
    key: domain.id,
    value: domain.name,
    text: domain.name,
  }));

  let fromControl = null;
  if (provider === EMAIL_SERVICES.SMTP) {
    fromControl = (
      <FormSelect
        name="smtp_config_id"
        label="From"
        required
        options={smtpOptions}
        placeholder="Select an address…"
      />
    );
  } else if (provider === EMAIL_SERVICES.MAILGUN) {
    fromControl = (
      <>
        <FormInput
          name="fromUser"
          label="From"
          required
          placeholder="username"
        />
        <FormSelect
          name="mailgunDomain"
          label="Domain"
          required
          options={mailgunOptions}
          placeholder="Select a domain…"
        />
      </>
    );
  } else if (providerValue != null) {
    fromControl = (
      <FormInput name="fromUser" label="From" required placeholder="username" />
    );
  }

  return (
    <div className="tw:grid tw:grid-cols-1 tw:gap-5 tw:sm:grid-cols-2">
      <FormInput name="senderName" label="Sender Name" required />
      <FormSelect
        name="provider"
        label="Email Provider"
        required
        options={providerOptions}
        placeholder="Select a provider…"
        onValueChange={onProviderChange}
      />
      {fromControl}
    </div>
  );
};

const ContactEmailFormFields = ({
  campaign,
  isCampaignError,
  onCampaignChange,
  campaignOptions,
  providerOptions,
  smtpOptions,
  recipientOptions,
  templates,
  footerOptions,
  signatureOptions,
}) => {
  const { setValue } = useFormContext();

  const providerValue = useWatch({ name: "provider" });
  const templateId = useWatch({ name: "template" });
  const footerId = useWatch({ name: "footerId" });
  const signatureId = useWatch({ name: "signatureId" });

  const campaignEmailService = campaign?.email_service ?? null;
  const provider = getResolvedProvider(providerValue, campaignEmailService);

  const templateOptions = createTemplateOptions(
    templates,
    campaign?.info?.selected_templates ?? [],
    template => isTemplateDisabled(template, provider)
  )
    // FormSelect has no disabled-item affordance, so provider-incompatible
    // and empty templates are dropped from the list rather than greyed.
    .filter(option => !option.disabled)
    .map(option => ({
      key: option.value,
      value: option.value,
      text: option.text,
    }));

  const { data: selectedTemplate } = useQuery({
    queryKey: ["email-template", templateId],
    queryFn: () => EmailTemplateService.getEmailTemplate(templateId),
    enabled: templateId != null,
  });
  const { data: selectedFooter } = useQuery({
    queryKey: ["email-footer", footerId],
    queryFn: () => EmailFooterService.getEmailFooter(footerId),
    enabled: footerId != null,
  });
  const { data: selectedSignature } = useQuery({
    queryKey: ["email-signature", signatureId],
    queryFn: () => EmailSignatureService.getEmailSignature(signatureId),
    enabled: signatureId != null,
  });

  const previewHtml = useMemo(
    () =>
      buildPreviewHtml(
        selectedTemplate?.html,
        selectedFooter?.html,
        selectedSignature?.html
      ),
    [selectedTemplate, selectedFooter, selectedSignature]
  );

  const handleCampaignChange = nextCampaignId => {
    // The provider and template options depend on the campaign, so a campaign
    // change clears the dependent selections.
    setValue("provider", null);
    setValue("smtp_config_id", null);
    setValue("mailgunDomain", null);
    setValue("fromUser", "");
    setValue("template", null);
    setValue("footerId", null);
    setValue("signatureId", null);
    onCampaignChange(nextCampaignId);
  };

  const handleProviderChange = nextProviderValue => {
    setValue("smtp_config_id", null);
    setValue("mailgunDomain", null);
    setValue("fromUser", "");
    // Drop the template if the new provider disables it (a stale id would
    // otherwise pass validation and reach the payload).
    const current = templates.find(template => template.id === templateId);
    const nextProvider = getResolvedProvider(
      nextProviderValue,
      campaignEmailService
    );
    if (current && isTemplateDisabled(current, nextProvider)) {
      setValue("template", null);
    }
  };

  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      <Card>
        <CardHeader>
          <CardTitle>Campaign</CardTitle>
        </CardHeader>
        <CardContent>
          <FormSelect
            name="campaignId"
            label="Campaign"
            required
            options={campaignOptions}
            placeholder="Select a campaign…"
            onValueChange={handleCampaignChange}
          />
          {isCampaignError && <FieldError>Could not load campaign</FieldError>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Provider &amp; Sender</CardTitle>
        </CardHeader>
        <CardContent>
          <ProviderFields
            provider={provider}
            providerValue={providerValue}
            providerOptions={providerOptions}
            smtpOptions={smtpOptions}
            onProviderChange={handleProviderChange}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recipients</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="tw:flex tw:flex-col tw:gap-5">
            <FormMultiSelect
              name="emailAddressIds"
              label="To"
              required
              options={recipientOptions}
              placeholder="Select recipients…"
            />
            <div className="tw:grid tw:grid-cols-1 tw:gap-5 tw:sm:grid-cols-2">
              <FormInput name="cc" label="CC" placeholder="cc@example.com" />
              <FormInput name="bcc" label="BCC" placeholder="bcc@example.com" />
            </div>
            <div className="tw:flex tw:flex-col tw:gap-2">
              <FormCheckbox
                name="ccAccountManagers"
                label="CC account managers"
              />
              <FormCheckbox
                name="ccExternalAccountManagers"
                label="CC external account managers"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Template</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="tw:grid tw:grid-cols-1 tw:gap-5 tw:sm:grid-cols-3">
            <FormSelect
              name="template"
              label="Template"
              required
              options={templateOptions}
              placeholder="Select a template…"
            />
            <FormSelect
              name="footerId"
              label="Footer"
              options={footerOptions}
              clearable
              placeholder="None"
            />
            <FormSelect
              name="signatureId"
              label="Signature"
              options={signatureOptions}
              clearable
              placeholder="None"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Attachments</CardTitle>
        </CardHeader>
        <CardContent>
          <FormFileUpload name="attachments" />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Preview</CardTitle>
        </CardHeader>
        <CardContent>
          <EmailPreview
            subject={selectedTemplate?.subject ?? ""}
            previewHtml={previewHtml}
          />
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * BaseForm for composing and sending an email to a single contact.
 *
 * The option lists load first and gate the form on `ready`. The template,
 * footer, and signature HTML for the preview load on demand from the form's
 * watched values.
 */
const ContactEmailForm = ({ contactId, onSuccess, onCancel }) => {
  const [campaignId, setCampaignId] = useState(null);

  const {
    data: smtpAccounts = [],
    isSuccess: smtpLoaded,
    isError: smtpError,
  } = useQuery({
    queryKey: ["smtp-accounts"],
    queryFn: () => SmtpAccountService.getSmtpAccounts(),
    meta: { errorMessage: "Could not load SMTP accounts" },
  });

  const hasSmtp = smtpAccounts.length > 0;
  const {
    data: providersRaw,
    isSuccess: providersLoaded,
    isError: providersError,
  } = useQuery({
    queryKey: [
      "email-providers",
      { smtp: hasSmtp, isSchedule: false, onlySmtp: false },
    ],
    queryFn: () =>
      EmailProviderService.getEmailProviders(hasSmtp, false, false),
    enabled: smtpLoaded,
    meta: { errorMessage: "Could not load email providers" },
  });

  const {
    data: templates = [],
    isSuccess: templatesLoaded,
    isError: templatesError,
  } = useQuery({
    queryKey: ["email-templates", "for-filters"],
    queryFn: () => EmailTemplateService.getEmailTemplatesForFilters(),
    meta: { errorMessage: "Could not load email templates" },
  });
  const {
    data: footers = [],
    isSuccess: footersLoaded,
    isError: footersError,
  } = useQuery({
    queryKey: ["email-footers", "for-filters"],
    queryFn: () => EmailFooterService.getEmailFootersForFilters(),
    meta: { errorMessage: "Could not load email footers" },
  });
  const {
    data: signatures = [],
    isSuccess: signaturesLoaded,
    isError: signaturesError,
  } = useQuery({
    queryKey: ["email-signatures", "for-filters"],
    queryFn: () => EmailSignatureService.getEmailSignaturesForFilters(),
    meta: { errorMessage: "Could not load email signatures" },
  });
  const {
    data: campaigns = [],
    isSuccess: campaignsLoaded,
    isError: campaignsError,
  } = useQuery({
    queryKey: ["campaigns-for-filters", { contact_id: contactId }],
    queryFn: () =>
      CampaignService.getCampaignsForFilters({ contact_id: contactId }),
    meta: { errorMessage: "Could not load the contact's campaigns" },
  });
  const {
    data: emailAddresses = [],
    isSuccess: emailAddressesLoaded,
    isError: emailAddressesError,
  } = useQuery({
    queryKey: ["contact", contactId, "email-addresses"],
    queryFn: () => ContactService.getEmailAddresses(contactId),
    meta: { errorMessage: "Could not load the contact's email addresses" },
  });

  const { data: campaign, isError: isCampaignError } = useQuery({
    queryKey: ["campaign", String(campaignId)],
    queryFn: () => CampaignService.getCampaign(campaignId),
    enabled: campaignId != null,
  });

  const campaignEmailService = campaign?.email_service ?? null;
  const schema = useMemo(
    () => buildSchema(campaignEmailService),
    [campaignEmailService]
  );

  const sendEmail = useMutation({
    mutationFn: payload => ContactService.sendEmail(payload),
  });

  const hasLoadError =
    smtpError ||
    providersError ||
    templatesError ||
    footersError ||
    signaturesError ||
    campaignsError ||
    emailAddressesError;

  const ready =
    smtpLoaded &&
    providersLoaded &&
    templatesLoaded &&
    footersLoaded &&
    signaturesLoaded &&
    campaignsLoaded &&
    emailAddressesLoaded;

  if (hasLoadError) {
    return (
      <span className="tw:text-destructive">
        Could not load the email form.
      </span>
    );
  }

  if (!ready) {
    return <FormSkeleton sections={3} />;
  }

  // EmailProviderService returns an error object instead of throwing, so the
  // provider list can be a non-array even when the query succeeds.
  const providers = Array.isArray(providersRaw) ? providersRaw : [];
  // There is no campaign or Constant Contact context here, so "Campaign
  // Default" is always offered; it resolves to the selected campaign's service.
  const providerOptions = [DEFAULT_PROVIDER_OPTION, ...providers];

  const smtpOptions = smtpAccounts.map(account => ({
    key: account.id,
    value: account.id,
    text: account.return_addr,
  }));

  const recipients = emailAddresses.filter(
    address => address.enabled && address.subscribed
  );
  const recipientOptions = recipients.map(address => ({
    key: address.value,
    value: address.value,
    text: address.text,
  }));
  const primary = recipients.find(address => address.is_primary);

  const defaultValues = {
    campaignId: null,
    provider: null,
    senderName: useAuthStore.getState().userAuth?.full_name ?? "",
    fromUser: "",
    smtp_config_id: null,
    mailgunDomain: null,
    emailAddressIds: primary ? [primary.value] : [],
    cc: "",
    bcc: "",
    ccAccountManagers: false,
    ccExternalAccountManagers: false,
    template: null,
    footerId: null,
    signatureId: null,
    attachments: [],
  };

  return (
    <BaseForm
      schema={schema}
      defaultValues={defaultValues}
      onSubmit={values =>
        sendEmail.mutateAsync(
          buildSendPayload(
            values,
            contactId,
            smtpAccounts,
            campaignEmailService
          )
        )
      }
      onSuccess={onSuccess}
      onCancel={onCancel}
      submitButtonText="Send"
      submittingButtonText="Sending"
      confirmSubmit
      confirmText="Are you sure you want to send this email?"
      ignoreEnterKey
    >
      <ContactEmailFormFields
        campaign={campaign}
        isCampaignError={isCampaignError}
        onCampaignChange={setCampaignId}
        campaignOptions={toSelectOptions(campaigns)}
        providerOptions={providerOptions}
        smtpOptions={smtpOptions}
        recipientOptions={recipientOptions}
        templates={templates}
        footerOptions={toSelectOptions(footers)}
        signatureOptions={toSelectOptions(signatures)}
      />
    </BaseForm>
  );
};

export default ContactEmailForm;
